import React, { useEffect, useState } from "react";
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "components/ui/alert-dialog";
import { Button } from "components/ui/button";
import { Checkbox } from "components/ui/checkbox";
import { type DefaultFobType, fobTypeLabel } from "./fobTypes";

interface FobTypeChangeDialogProps {
  open: boolean;
  siteName: string;
  fromFobType: DefaultFobType;
  toFobType: DefaultFobType;
  saving: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}

/**
 * Confirms a change to a Schlage site's default fob type. The change reinterprets every physical key at the site,
 * so it spells out what that means and needs an explicit acknowledgement.
 */
export default function FobTypeChangeDialog({
  open,
  siteName,
  fromFobType,
  toFobType,
  saving,
  onCancel,
  onConfirm,
}: FobTypeChangeDialogProps) {
  const [acknowledged, setAcknowledged] = useState(false);

  useEffect(() => {
    if (open) {
      setAcknowledged(false);
    }
  }, [open]);

  const toLabel = fobTypeLabel(toFobType);

  return (
    <AlertDialog
      open={open}
      onOpenChange={(nextOpen) => {
        if (!nextOpen && !saving) {
          onCancel();
        }
      }}
    >
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Change default fob type?</AlertDialogTitle>
          <AlertDialogDescription>
            You're changing the default fob type for <strong>{siteName}</strong> from{" "}
            <strong>{fobTypeLabel(fromFobType)}</strong> to <strong>{toLabel}</strong>. This applies to every
            physical key at this site.
          </AlertDialogDescription>
        </AlertDialogHeader>

        <div className="grid gap-3 text-sm">
          <ul className="grid list-disc gap-1 pl-5">
            <li>The fob number shown for this site's keys may change.</li>
            <li>
              Keys that aren't {toLabel} fobs may stop working with third-party integrations that sync physical keys.
            </li>
            <li>Access at Schlage doors isn't affected.</li>
            <li>Existing keys aren't updated automatically; you may be offered a sync after saving.</li>
          </ul>

          <div className="flex items-center gap-2">
            <Checkbox
              checked={acknowledged}
              disabled={saving}
              id="fob-type-change-acknowledge"
              onCheckedChange={(checked) => setAcknowledged(checked === true)}
            />
            <label htmlFor="fob-type-change-acknowledge">
              I understand this changes how every key at this site is handled.
            </label>
          </div>
        </div>

        <AlertDialogFooter>
          <AlertDialogCancel disabled={saving}>Cancel</AlertDialogCancel>
          {/* A plain Button, not AlertDialogAction: Action closes the dialog on click, before the save finishes. */}
          <Button disabled={!acknowledged || saving} onClick={onConfirm} type="button">
            {saving ? "Saving..." : "Change fob type"}
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
